import styled, { keyframes } from 'styled-components';
import { THEME } from '@/common/styles/theme';

const fadeIn = keyframes`
	from {
		opacity: 0;
		transform: scale(0.96);
	}

	to {
		opacity: 1;
		transform: scale(1);
	}
`;

export const LanguageSwitcherRoot = styled.div`
	width: 100%;
	display: flex;
	justify-content: center;

	> button {
		width: 46px;
		height: 46px;
		border: 1px solid rgba(255, 255, 255, 0.14);
		border-radius: 14px;
		padding: 0;
		display: grid;
		place-items: center;
		background:
			radial-gradient(circle at 80% 0%, rgba(255, 77, 242, 0.12), transparent 34%),
			rgba(12, 5, 17, 0.96);
		box-shadow: 0 12px 28px rgba(0, 0, 0, 0.42);
		cursor: pointer;
		transition:
			border-color 160ms ease,
			box-shadow 160ms ease,
			transform 160ms ease;

		&:hover,
		&:focus-visible {
			border-color: rgba(255, 77, 242, 0.42);
			box-shadow: 0 16px 34px rgba(255, 77, 242, 0.18);
			transform: translateY(-1px);
		}
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: auto;

		> button {
			width: 34px;
			height: 34px;
			border-radius: 10px;
			box-shadow: 0 6px 14px rgba(0, 0, 0, 0.32);
		}
	}
`;

export const FlagWrap = styled.span`
	display: block;
	flex-shrink: 0;

	svg {
		width: 28px;
		height: 21px;
		border-radius: 5px;
		display: block;
		box-shadow:
			inset 0 0 0 1px rgba(255, 255, 255, 0.22),
			0 2px 8px rgba(0, 0, 0, 0.22);
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		svg {
			width: 20px;
			height: 14px;
			border-radius: 3px;
		}
	}
`;

export const LanguageOverlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 5000;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 16px;
	background: rgba(2, 1, 6, 0.55);
	backdrop-filter: blur(6px);
`;

export const LanguageModal = styled.div`
	width: min(calc(100vw - 32px), 420px);
	padding: 24px 22px 20px;
	border: 1px solid ${THEME.line};
	border-radius: 20px;
	background:
		radial-gradient(circle at 80% 0%, rgba(255, 77, 242, 0.14), transparent 38%),
		radial-gradient(circle at 0% 100%, rgba(176, 38, 255, 0.08), transparent 42%),
		rgba(12, 5, 17, 0.98);
	box-shadow: ${THEME.shadow};
	animation: ${fadeIn} 0.18s ease-out;
`;

export const LanguageModalHeader = styled.div`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 10px;
`;

export const LanguageHeaderCopy = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	min-width: 0;
`;

export const LanguageTitle = styled.h3`
	margin: 0;
	font-family: ${THEME.fontApp};
	font-size: 1.2rem;
	font-weight: 800;
	line-height: 1.2;
	color: ${THEME.text};
`;

export const LanguageSubtitle = styled.p`
	margin: 0;
	font-family: ${THEME.fontBody};
	font-size: 0.92rem;
	font-weight: 700;
	line-height: 1.35;
	color: ${THEME.muted};
`;

export const LanguageDescription = styled.p`
	margin: 0 0 18px;
	font-family: ${THEME.fontBody};
	font-size: 0.84rem;
	line-height: 1.55;
	color: ${THEME.soft};
`;

export const LanguageCloseButton = styled.button`
	width: 34px;
	height: 34px;
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 10px;
	padding: 0;
	flex-shrink: 0;
	display: grid;
	place-items: center;
	background: rgba(255, 255, 255, 0.04);
	color: ${THEME.muted};
	font-size: 1.35rem;
	line-height: 1;
	cursor: pointer;
	transition:
		border-color 160ms ease,
		color 160ms ease,
		background 160ms ease;

	&:hover,
	&:focus-visible {
		border-color: rgba(255, 77, 242, 0.35);
		color: ${THEME.text};
		background: rgba(255, 77, 242, 0.08);
	}
`;

export const LanguageOptions = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
`;

export const LanguageOption = styled.button`
	width: 100%;
	border: 1px solid ${(props) => (props.$active ? 'rgba(255, 77, 242, 0.58)' : 'rgba(255, 255, 255, 0.1)')};
	border-radius: 14px;
	padding: 12px 14px;
	color: ${THEME.text};
	background: ${(props) =>
		props.$active
			? 'linear-gradient(135deg, rgba(255, 77, 242, 0.16), rgba(255, 255, 255, 0.05))'
			: 'linear-gradient(135deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.02))'};
	display: flex;
	align-items: center;
	gap: 12px;
	text-align: left;
	cursor: pointer;
	transition:
		border-color 160ms ease,
		box-shadow 160ms ease,
		transform 160ms ease,
		background 160ms ease;

	&:hover,
	&:focus-visible {
		border-color: rgba(255, 77, 242, 0.42);
		box-shadow: 0 0 18px rgba(255, 77, 242, 0.12);
		transform: translateY(-1px);
	}
`;

export const LanguageOptionCopy = styled.div`
	display: flex;
	flex-direction: column;
	gap: 3px;
	flex: 1;
	min-width: 0;
`;

export const LanguageOptionName = styled.strong`
	font-family: ${THEME.fontApp};
	font-size: 0.95rem;
	font-weight: 800;
	line-height: 1.2;
	color: ${THEME.text};
`;

export const LanguageOptionDescription = styled.span`
	font-family: ${THEME.fontBody};
	font-size: 0.78rem;
	line-height: 1.4;
	color: ${THEME.soft};
`;

export const LanguageOptionBadge = styled.span`
	flex-shrink: 0;
	padding: 4px 10px;
	border-radius: 999px;
	border: 1px solid rgba(255, 77, 242, 0.35);
	background: rgba(255, 77, 242, 0.12);
	font-family: ${THEME.fontApp};
	font-size: 0.68rem;
	font-weight: 800;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	color: rgba(255, 247, 255, 0.92);
`;
